import { useState, useRef } from 'react';
import SandhillsReCAPTCHA from '@Components/Shared/SandhillsReCAPTCHA';
import { gtmPushEvent, sandhillsRequest, focusErroredField } from '@Components/Utilities/SandhillsUtility';
import TextField from '@Components/Shared/Controls/TextField';
import TranslationText from '@Components/Utilities/TranslationText';
import SelectField from '@Components/Shared/Controls/SelectField';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import CallToActionModal from '@Components/Shared/Modals/CallToActionModal';
import SimpleHomeBreadcrumb from '../../Components/Shared/SimpleHomeBreadcrumb';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import atoms from '@Components/Utilities/SandhillsAtoms';
import { useRecoilValue } from 'recoil';

const LivestockBasedAdvertisingView = (props) => {

    const recaptchaRef = useRef(null);
    const [captchaOpen, setCaptchaOpen] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);

    const [formData, setFormData] = useState({
        FirstName: props.FirstName || '',
        LastName: props.LastName || '',
        Email: props.Email || '',
        Phone: props.Phone || '',
        Company: props.Company || '',
        City: props.City || '',
        State: props.State || '',
        Country: '',
        PostalCode: props.PostalCode || '',
        Message: props.Message || '',
        IndustryOptions: props.IndustryOptions || [],
        HowCanWeHelpYouItems: props.HowCanWeHelpYouItems || [],
        ModelType: props.ModelType
    });

    const [modalData, setModalData] = useState(null);
    const closeModal = () => setModalData(null);
    const scrollToForm = () => {
        setModalData(null);
        inputReferences.current[`FirstName`].scrollIntoView({ behavior: 'smooth' });
        setTimeout(() => { inputReferences.current[`FirstName`].focus(); }, 500);
    }

    const openCallToActionModal = (modal, howCanWeHelpYouValue) => {
        setModalData(modal);
        onCheckboxChange({
            target: {
                value: howCanWeHelpYouValue,
                checked: true,
                uncheckOtherSelections: true
            }
        });
    }

    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('Email', 'Email', ValidationRules.requiredOr('Phone', 'Phone')),
            ruleRunner('Phone', 'Phone', ValidationRules.requiredOr('Email', 'Email')),
            ruleRunner('Message', 'Message', ValidationRules.required),
        ]);

        if (!formData.IndustryOptions?.some(i => i.Selected)) {
            errors.Industry = "Industry is required";
            inputReferences?.current['Industry']?.focus();
        }

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);

        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            if (props.Captcha && recaptchaRef.current) {
                setCaptchaOpen(true);
                recaptchaRef.current.execute();
            } else {
                onSubmit();
            }
        }
    };

    const onSubmit = (token) => {
        setIsLoading(true);
        sandhillsRequest({
            url: routes.SubmitAdvertising,
            method: 'post',
            data: {
                ...formData,
                Captcha: {
                    Response: token
                },
                webAgreementTypeIDs: props.WebAgreementTypeIDs
            },
            errorHandling: {
                useConsole: false,
                useAlert: false
            }
        }).then((response) => {
            setIsLoading(false);
            if (response.Success) {
                gtmPushEvent('advertising-form');

                setFormData({
                    FirstName: response.FirstName || '',
                    LastName: response.LastName || '',
                    Email: response.Email || '',
                    Phone: response.Phone || '',
                    Company: response.Company || '',
                    City: response.City || '',
                    State: response.State || '',
                    Country: '',
                    PostalCode: response.PostalCode || '',
                    Message: response.Message || '',
                    IndustryOptions: props.IndustryOptions || [],
                    HowCanWeHelpYouItems: props.HowCanWeHelpYouItems || [],
                    ModelType: response.ModelType
                });

                setSuccess(response.Success);
                setSubmitted(true);

                if (response.UseLeadsUXUpdate && response.Success) {
                    let feedbackForm = document.getElementById('feedbackForm');
                    if (feedbackForm) feedbackForm.style.display = 'none';
                    setTimeout(() => {
                        if (feedbackForm) feedbackForm.style.display = '';
                        setSuccess(!response.Success);
                        setSubmitted(false);
                    }, 3000);
                }
            }

            setValidationErrors(response.ValidationErrors || {});
            focusErroredField(response.ValidationErrors, inputReferences);

            if (response.Captcha && recaptchaRef.current) {
                setCaptchaOpen(false);
                recaptchaRef.current.reset();
            }
        });
    };

    const onTextChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const onIndustryChange = (e) => {
        let value = e.target.value;
        setFormData(previousState => {
            return {
                ...previousState,
                IndustryOptions: [...previousState.IndustryOptions].map(option => {
                    if (option.Value == value) {
                        return { ...option, Selected: true };
                    } else {
                        return { ...option, Selected: false };
                    }
                })
            };
        });
    };

    const onCheckboxChange = (e) => {
        let value = e.target.value;
        let checked = e.target.checked;
        let uncheckOtherSelections = e.target.uncheckOtherSelections;
        setFormData(previousState => {
            return {
                ...previousState,
                HowCanWeHelpYouItems: [...previousState.HowCanWeHelpYouItems].map(item => {
                    if (item.Value == value) {
                        return { ...item, Selected: checked };
                    } else {
                        if (uncheckOtherSelections) {
                            return { ...item, Selected: false };
                        }
                        else {
                            return { ...item };
                        }
                    }
                })
            };
        });
    };

    const resetSubmit = () => {
        setIsLoading(false);
    };

    return (
        <div className='advertising'>
            <div className="info-body-container">
                <div className='info-content-header advertising-container'>
                    <SimpleHomeBreadcrumb homeRoute={props.BreadCrumbHomeRoute} displayText={<TranslationText tKey='sandhills.tradepubwebsite.mvc.advertising' />} />
                </div>
                <h1 className='pagename border-bottom advertising-container'>Advertising With {props.CurrentSiteDomain}</h1>
                <div className='info-content-container'>
                    <div className='advertising-container'>
                        <div className="advertising-container cf">
                            <div className="advertising__banner">
                                <h2 className="banner-title">Sell {props.DefaultCategoryName} &amp; Hay on your terms</h2>
                                <ul className="advertising__bullet-list">
                                    {props.BannerSubtitleList.map((subtitle) =>
                                        <li>{subtitle}</li>
                                    )}
                                </ul>
                            </div>
                            {props.CurrentWebsiteName == "LivestockMarket" &&
                                <div className='description-container border-bottom'>
                                    <div className='advertising-description'>
                                        <div dangerouslySetInnerHTML={{ __html: props.AdvertisingDescription }}></div><br />
                                    </div>
                                    {props.AdvertisingImageUrl &&
                                        <div className='advertising-image'>
                                            <img src={props.AdvertisingImageUrl} alt='Advertising' />
                                        </div>}
                                </div>}
                            <div className="advertising-icons">
                                {(props.CallToActionModals && props.CallToActionModals.length > 0) &&
                                    props.CallToActionModals.map((item) =>
                                        <button className='advertising-icons__button ts-button' onClick={() => openCallToActionModal(item.Modal, item.HowCanWeHelpYouValue)}>
                                            <LazyLoadImage src={item.Button.ImageSourceUrl} alt={item.Button.Header} className='advertising-icons__image' />
                                            <strong className='advertising-icons__heading'>{item.Button.Header}</strong>
                                            <p className='advertising-icons__text'>{item.Button.Description}</p>
                                        </button>
                                    )
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='advertising-form'>
                <div className='info-content-container cf'>
                    <h2>Interested in Advertising with {props.CurrentWebsiteName} or Ready to Learn More?</h2>
                    <h3 className='bottom-padding'>Please call: <a href={'tel:+' + props.ContactInformation.UnformmatedTollFreePhoneNumber}><span className='primary-color'>{props.ContactInformation.SiteTollFreePhoneNumber}</span></a> or fill out the form below:</h3>
                    {props.UseLeadsUXUpdate && success &&
                        <div className='success' style={{ textAlign: 'center' }}>Successfully sent inquiry!</div>}
                    <form id='feedbackForm' onSubmit={submitForm}>
                        <ValidationErrors errors={validationErrors}/>
                        <div className="advertising__form">
                            <div className='advertising__form-column'>
                                <TextField
                                    id='FirstName'
                                    name='FirstName'
                                    value={formData.FirstName}
                                    placeholder='First Name'
                                    type='text'
                                    label='First Name'
                                    class='advertising__form-input'
                                    validationErrors={validationErrors.FirstName}
                                    ref={(element) => inputReferences.current[`FirstName`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='LastName'
                                    name='LastName'
                                    value={formData.LastName}
                                    placeholder='Last Name'
                                    type='text'
                                    label='Last Name'
                                    class='advertising__form-input'
                                    validationErrors={validationErrors.LastName}
                                    ref={(element) => inputReferences.current[`LastName`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Email'
                                    name='Email'
                                    value={formData.Email}
                                    placeholder='Email'
                                    type='text'
                                    label='Email'
                                    class='advertising__form-input'
                                    required={true}
                                    validationErrors={validationErrors.Email}
                                    ref={(element) => inputReferences.current[`Email`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Phone'
                                    name='Phone'
                                    value={formData.Phone}
                                    placeholder='(000) 000-0000'
                                    type='text'
                                    label='Phone'
                                    class='advertising__form-input'
                                    required={true}
                                    validationErrors={validationErrors.Phone}
                                    ref={(element) => inputReferences.current[`Phone`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='Company'
                                    name='Company'
                                    value={formData.Company}
                                    placeholder='Company'
                                    type='text'
                                    label='Company'
                                    class='advertising__form-input'
                                    validationErrors={validationErrors.Company}
                                    ref={(element) => inputReferences.current[`Company`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <TextField
                                    id='City'
                                    name='City'
                                    value={formData.City}
                                    placeholder='City'
                                    type='text'
                                    label='City'
                                    class='advertising__form-input'
                                    validationErrors={validationErrors.City}
                                    ref={(element) => inputReferences.current[`City`] = element}
                                    onFieldChanged={onTextChange}
                                />
                            </div>
                            <div className='advertising__form-column'>
                                {props.GetStateDropDown && props.GetStateDropDown.length > 0 &&
                                    <SelectField
                                        id='State'
                                        name='State'
                                        value={formData.State}
                                        options={props.GetStateDropDown}
                                        label='State'
                                        class='advertising__form-input'
                                        selectClass='select-dropdown'
                                        validationErrors={validationErrors.State}
                                        ref={(element) => inputReferences.current[`State`] = element}
                                        onFieldChanged={onTextChange}
                                    />}
                                <TextField
                                    id='Country'
                                    name='Country'
                                    value={formData.Country}
                                    placeholder='Country'
                                    type='text'
                                    label={<TranslationText tKey='sandhills.country' />}
                                    class='advertising__form-input hidden'
                                    required={false}
                                    validationErrors={validationErrors.Country}
                                    ref={(element) => inputReferences.current[`Country`] = element}
                                    onFieldChanged={onTextChange}
                                    htmlAutoComplete='chrome-off'
                                />
                                <TextField
                                    id='PostalCode'
                                    name='PostalCode'
                                    value={formData.PostalCode}
                                    placeholder='Postal Code'
                                    type='text'
                                    label='Postal Code'
                                    class='advertising__form-input'
                                    validationErrors={validationErrors.PostalCode}
                                    ref={(element) => inputReferences.current[`PostalCode`] = element}
                                    onFieldChanged={onTextChange}
                                />
                                <SelectField
                                    id='Industry'
                                    name='Industry'
                                    value={formData.Industry}
                                    options={props.IndustryOptions}
                                    label='Industry'
                                    class='advertising__form-input'
                                    selectClass='select-dropdown'
                                    required={true}
                                    validationErrors={validationErrors.Industry}
                                    ref={(element) => inputReferences.current[`Industry`] = element}
                                    onFieldChanged={onIndustryChange}
                                />
                                <TextField
                                    id='Message'
                                    name='Message'
                                    value={formData.Message}
                                    placeholder={translator.translate('sandhills.tradesites.mvc.enteryourmessageupto500characters')}
                                    type='textarea'
                                    label={<TranslationText tKey='sandhills.tradepubwebsite.mvc.message' />}
                                    class='advertising__form-input advertising__form-input_textarea'
                                    inputClass='text-area'
                                    required={true}
                                    validationErrors={validationErrors.Message}
                                    ref={(element) => inputReferences.current[`Message`] = element}
                                    onFieldChanged={onTextChange}
                                />
                            </div>
                            {(formData.HowCanWeHelpYouItems && formData.HowCanWeHelpYouItems.length > 0) &&
                                <div className='advertising__form-column'>
                                    <fieldset>
                                        <legend className='advertising__form-request-type'>How Can We Help You?</legend>
                                        {formData.HowCanWeHelpYouItems.map(item =>
                                            <div className='checkbox-container'>
                                                <input id={'HowCanWeHelpYouItems-' + item.Text} name={'HowCanWeHelpYouItems-' + item.Text} className='checkbox' value={item.Value} type='checkbox' checked={item.Selected} onChange={onCheckboxChange} />
                                                <label htmlFor={'HowCanWeHelpYouItems-' + item.Text}>{item.Text}</label>
                                            </div>
                                        )}
                                        <div className='error'>{validationErrors.CheckboxError}</div>
                                    </fieldset>
                                </div>
                            }
                        </div>
                        <div className='clear' />
                        <div className='column12 left'>
                            {submitted &&
                                (success ?
                                    <div className='success'>Successfully sent inquiry!</div> :
                                    <div className='error'>An error occured, please try again.</div>)}
                        </div>
                        <div className='clear' />
                        <div className='column12 left'>
                            <div className='recaptcha-submit-container'>
                                <div className='captcha-placeholder'>
                                    {props.Captcha &&
                                        <SandhillsReCAPTCHA
                                            ref={recaptchaRef}
                                            sitekey={props.Captcha.Key}
                                            onChange={onSubmit}
                                            onCaptchaClosedBeforeCompletion={resetSubmit}
                                            captchaOpen={captchaOpen}
                                        />}
                                </div>
                                <div dangerouslySetInnerHTML={{ __html: props.FeedbackPolicyNotice }} style={{ 'font-size': '.75em', 'margin-top': '10px' }} />
                                <input className='recaptcha-submit-button button ts-button' type='submit' id='advertising-submit-button' name='advertising-submit-button' value='Submit' />
                            </div>
                        </div>
                    </form>
                </div>
            </div>
            {modalData &&
                <CallToActionModal {...modalData} closeModal={closeModal} scrollToForm={scrollToForm} CurrentWebsiteName={props.CurrentWebsiteName} />
            }
            <Backdrop
                open={isLoading}
                sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
                <CircularProgress />
            </Backdrop>
        </div>

    );
};

export default LivestockBasedAdvertisingView;
global.LivestockBasedAdvertisingView = LivestockBasedAdvertisingView;